// "Integraciones" tab of the event detail panel — Momentus only, read-only
// against Momentus (the dashboard never calls Momentus directly, only this
// backend). Reuses the same visual language as the rest of EventDetail.jsx
// (evd-ir / evd-btn-* / evd-branch-option / evd-empty-hint classes, no new
// CSS file).
//
// The event picker is a search box (POST /v1/events/query via
// MomentusFetchAPI.searchEvents), not a preloaded <select>: a real Venue
// tested during implementation had 2500+ historical events, and fetching
// "all of them" up front was slow enough to time out. Searching as the user
// types is both faster and matches how Momentus itself expects this to be
// queried (search by name/external id/unique number).
import { useEffect, useRef, useState } from "react";
import { MomentusFetchAPI } from "../../../api/Momentus";
import AlertSmall from "../../Alerts/AlertSmall";

const STATUS = {
  LOADING: 'loading',
  DISABLED: 'disabled',
  NOT_CONFIGURED: 'not_configured',
  VENUE_NOT_CONFIGURED: 'venue_not_configured',
  READY: 'ready',
  ERROR: 'error'
};

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_CHARS = 2;

// event_integrations.metadata is a MySQL JSON column populated on link()
// with { name, start, end } — mysql2 normally returns it already parsed,
// but this tolerates it coming back as a raw string too.
const getLinkedEventName = (link) => {
  let metadata = link.metadata;
  if (typeof metadata === 'string') {
    try { metadata = JSON.parse(metadata); } catch (e) { metadata = null; }
  }
  if (metadata && metadata.name) {
    return `${metadata.name} (${link.external_event_id})`;
  }
  return link.external_event_id;
};

export default function MomentusEventPanel({ eventId }) {
  const token = localStorage.getItem('x-access-token');

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState('');
  const [config, setConfig] = useState(null);

  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadConfig = async () => {
    setStatus(STATUS.LOADING);
    try {
      const res = await MomentusFetchAPI.getConfig(token);
      const cfg = res.data.config;
      setConfig(cfg);

      if (!cfg.enabled) { setStatus(STATUS.DISABLED); return; }
      if (!cfg.configured) { setStatus(STATUS.NOT_CONFIGURED); return; }
      if (!cfg.venueId) { setStatus(STATUS.VENUE_NOT_CONFIGURED); return; }
      setStatus(STATUS.READY);
    } catch (err) {
      setErrorMessage((err.response && err.response.data && err.response.data.error) || 'No se pudo leer la configuración de Momentus');
      setStatus(STATUS.ERROR);
    }
  };

  const loadLink = async () => {
    setLinkLoading(true);
    try {
      const res = await MomentusFetchAPI.getEventLink(eventId, token);
      setLink(res.data.linked ? res.data.link : null);
    } catch (err) {
      setLink(null);
    } finally {
      setLinkLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === STATUS.READY) loadLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Debounced search-as-you-type against Momentus, scoped to the
  // business's configured Venue on the backend.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchText || searchText.trim().length < MIN_SEARCH_CHARS) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await MomentusFetchAPI.searchEvents(token, searchText.trim());
        setSearchResults(res.data.events || []);
      } catch (err) {
        setSearchResults([]);
        AlertSmall({ message: (err.response && err.response.data && err.response.data.error) || 'No se pudieron buscar eventos en Momentus', icon: 'error' });
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleSelectResult = (momentusEvent) => {
    setSelectedEvent(momentusEvent);
    setSearchText(momentusEvent.name);
    setSearchResults([]);
  };

  const handleLink = async () => {
    if (!selectedEvent) {
      AlertSmall({ message: 'Busque y seleccione un evento de Momentus', icon: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await MomentusFetchAPI.linkEvent(eventId, {
        external_event_id: selectedEvent.id,
        external_venue_id: selectedEvent.venueIds ? selectedEvent.venueIds[0] : (config && config.venueId),
        metadata: { name: selectedEvent.name, start: selectedEvent.start, end: selectedEvent.end }
      }, token);
      AlertSmall({ message: 'Evento vinculado a Momentus', icon: 'success' });
      setSelectedEvent(null);
      setSearchText('');
      await loadLink();
    } catch (err) {
      AlertSmall({ message: (err.response && err.response.data && err.response.data.error) || 'No se pudo vincular el evento', icon: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    setSaving(true);
    try {
      await MomentusFetchAPI.unlinkEvent(eventId, token);
      AlertSmall({ message: 'Vínculo con Momentus eliminado', icon: 'success' });
      setLink(null);
      setSelectedEvent(null);
      setSearchText('');
    } catch (err) {
      AlertSmall({ message: (err.response && err.response.data && err.response.data.error) || 'No se pudo desvincular el evento', icon: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (status === STATUS.LOADING) {
    return <p className="evd-empty-hint">Cargando estado de Momentus...</p>;
  }

  if (status === STATUS.ERROR) {
    return <p className="evd-empty-hint">{errorMessage}</p>;
  }

  if (status === STATUS.DISABLED) {
    return (
      <div className="evd-ir evd-static">
        <div className="evd-ir-content">
          <span className="evd-ir-label">Momentus</span>
          <span className="evd-ir-value">No habilitado para esta empresa</span>
        </div>
      </div>
    );
  }

  if (status === STATUS.NOT_CONFIGURED) {
    return (
      <div className="evd-ir evd-static">
        <div className="evd-ir-content">
          <span className="evd-ir-label">Momentus</span>
          <span className="evd-ir-value">Habilitado, pero falta configurar credenciales/host</span>
        </div>
      </div>
    );
  }

  if (status === STATUS.VENUE_NOT_CONFIGURED) {
    return (
      <div className="evd-ir evd-static">
        <div className="evd-ir-content">
          <span className="evd-ir-label">Momentus</span>
          <span className="evd-ir-value">Habilitado, pero falta seleccionar un Venue</span>
        </div>
      </div>
    );
  }

  // STATUS.READY
  return (
    <>
      <div className="evd-ir evd-static">
        <div className="evd-ir-content">
          <span className="evd-ir-label">Momentus</span>
          <span className="evd-ir-value">CONECTADO</span>
        </div>
      </div>

      <div className="evd-ir evd-static">
        <div className="evd-ir-content">
          <span className="evd-ir-label">Venue</span>
          <span className="evd-ir-value">{config.venueName || config.venueId}</span>
        </div>
      </div>

      {linkLoading ? (
        <p className="evd-empty-hint">Consultando vínculo actual...</p>
      ) : link ? (
        <div className="evd-ir evd-static">
          <div className="evd-ir-content">
            <span className="evd-ir-label">Evento Momentus vinculado</span>
            <span className="evd-ir-value">{getLinkedEventName(link)}</span>
          </div>
        </div>
      ) : (
        <p className="evd-empty-hint">Este evento todavía no está vinculado a Momentus.</p>
      )}

      <div style={{ position: 'relative' }}>
        <label className="evd-edit-label" htmlFor="momentus-event-search">Buscar evento en Momentus</label>
        <input
          id="momentus-event-search"
          className="evd-input"
          type="text"
          placeholder="Escriba el nombre, id externo o número del evento..."
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setSelectedEvent(null); }}
          autoComplete="off"
        />

        {searching && <p className="evd-empty-hint" style={{ marginTop: '.35rem' }}>Buscando...</p>}

        {!searching && searchResults.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', marginTop: '.5rem' }}>
            {searchResults.map((mEvent) => (
              <button
                key={mEvent.id}
                type="button"
                className="evd-branch-option"
                onClick={() => handleSelectResult(mEvent)}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="evd-branch-name">{mEvent.name}</span>
                  <span className="evd-branch-address">{mEvent.start} — {mEvent.uniqueNumber}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {!searching && searchText.trim().length >= MIN_SEARCH_CHARS && searchResults.length === 0 && !selectedEvent && (
          <p className="evd-empty-hint" style={{ marginTop: '.35rem' }}>Sin resultados en Momentus para "{searchText}".</p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '.5rem' }}>
        <button className="evd-btn-primary" disabled={saving || !selectedEvent} onClick={handleLink}>
          {link ? 'Cambiar vínculo' : 'Vincular evento'}
        </button>
        {link && (
          <button className="evd-btn-danger" disabled={saving} onClick={handleUnlink}>
            Desvincular
          </button>
        )}
      </div>
    </>
  );
}
